import type { ITeacherStudent } from '@/types/teacher';

import { useMemo, Dispatch, SetStateAction } from 'react';

import { UserAvatarList } from '@/components/Avatar';
import ProgressBar from '@/components/ProgressBar';
import CustomTable from '@/components/Table';
import { LessonStatusConfig } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import { getAssetUrl, formatLastActive } from '@/utils';

interface IStudentProgressTableProps {
  students: ITeacherStudent[];
  total: number;
  sortField: string;
  sortDir: string;
  onSort: (key: string) => void;
  isLoading?: boolean;
  onRowClick?: (id: string) => void;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

const StudentProgressTable = ({
  students,
  total,
  sortField,
  sortDir,
  onSort,
  isLoading,
  onRowClick,
  currentPage,
  setCurrentPage,
}: IStudentProgressTableProps) => {
  const { formatText } = useFormatText();

  const columns = useMemo(
    () => [
      {
        key: 'student_name',
        title: 'teacher-details.student-table.columns.student-name',
        sorter: true,
        handleSort: () => onSort('student_name'),
        width: '200px',
        render: (record: ITeacherStudent) => (
          <div className="flex items-center gap-3">
            <UserAvatarList
              user={record.student_name}
              image={
                getAssetUrl(record?.asset) || getAssetUrl(record?.profile_pic)
              }
            />
            <span className="text-neutral-primary font-medium text-sm truncate max-w-40">
              {record.student_name}
            </span>
          </div>
        ),
      },
      {
        key: 'class_name',
        title: 'teacher-details.student-table.columns.assigned-class',
        sorter: true,
        handleSort: () => onSort('class_name'),
        width: '140px',
        render: (record: ITeacherStudent) => (
          <span className="text-neutral-secondary text-sm">
            {record.class_name}
          </span>
        ),
      },
      {
        key: 'lessons_count',
        title: 'teacher-details.student-table.columns.lessons',
        sorter: true,
        handleSort: () => onSort('lessons_count'),
        align: 'start' as const,
        width: '100px',
        render: (record: ITeacherStudent) => (
          <span className="text-pink-600 font-semibold text-sm py-0.5 px-2 rounded-full flex items-center bg-pink-50 w-fit">
            {record.lessons_count}
          </span>
        ),
      },
      {
        key: 'avg_progress',
        title: 'teacher-details.student-table.columns.progress',
        width: '200px',
        sorter: true,
        handleSort: () => onSort('avg_progress'),
        render: (record: ITeacherStudent) => (
          <ProgressBar
            value={record?.avg_progress ?? 0}
            barColor="bg-brand-default"
          />
        ),
      },
      {
        key: 'accuracy',
        title: 'teacher-details.student-table.columns.accuracy',
        width: '120px',
        sorter: true,
        handleSort: () => onSort('accuracy'),
        align: 'center' as const,
        render: (record: ITeacherStudent) => (
          <span className="text-neutral-primary text-sm font-medium">
            {record.accuracy ?? 0}%
          </span>
        ),
      },
      {
        key: 'status',
        title: 'teacher-details.student-table.columns.status',
        width: '130px',
        sorter: true,
        handleSort: () => onSort('status'),
        align: 'center' as const,
        render: (record: ITeacherStudent) => {
          const config =
            LessonStatusConfig[
              record?.status as keyof typeof LessonStatusConfig
            ];
          return config ? (
            <span
              className={`text-xs font-semibold py-1 px-2.5 rounded-full ${config.className}`}
            >
              {formatText(config.label)}
            </span>
          ) : (
            <span>-</span>
          );
        },
      },
      {
        key: 'last_active',
        title: 'teacher-details.student-table.columns.last-active',
        sorter: true,
        handleSort: () => onSort('last_active'),
        width: '120px',
        render: (record: ITeacherStudent) => (
          <span className="text-neutral-secondary text-sm whitespace-nowrap">
            {formatLastActive(
              record.last_active ? Number(record.last_active) : 0,
              formatText
            )}
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortField, sortDir, formatText]
  );

  return (
    <div className="mt-6">
      <h3 className="text-base font-semibold text-neutral-primary mb-4">
        {formatText('teacher-details.student-progress-overview')}
      </h3>

      <CustomTable
        dataSource={students}
        columns={columns}
        rowKey="id"
        total={total}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        fieldName={sortField}
        sortDirection={sortDir}
        isLoading={isLoading}
        dataTestId="student-progress-table"
        onRowClick={onRowClick}
      />
    </div>
  );
};

export default StudentProgressTable;
